import { observable, action, makeObservable } from 'mobx';
import { SidePanelListType, WorkflowStatus, WorkflowStatusListType } from 'typings/enums';
import { ManagerCardWorkflowStatus } from 'nomst/Stores/Decision/ManagerListSidePanel/managerCardWorkflowStatus';
import { ManagerCardOverBudget } from 'nomst/Stores/Decision/ManagerListSidePanel/managerCardOverBudget';
import { workflowStatusFromListTypeMap } from 'nomst/Stores/Decision/Constants/workflowStatusMap';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import * as maps from 'nomst/Stores/Decision/Constants/workflowStatusMap';
import { IWorkflowStatusManager } from 'typings/manager';

export class ManagerCardsContainer {
    private _masterAccountId: string;
    private _cycleId: number;

    @observable private _workflowStatusManagers: Map<WorkflowStatus, ManagerCardWorkflowStatus[]>;

    @observable private _overBudgetManagers: Map<number, ManagerCardOverBudget[]>;

    constructor(masterAccountId: string, cycleId: number) {
        makeObservable(this);
        this._masterAccountId = masterAccountId;
        this._cycleId = cycleId;
        this._workflowStatusManagers = new Map<WorkflowStatus, ManagerCardWorkflowStatus[]>();
        this._overBudgetManagers = new Map<number, ManagerCardOverBudget[]>();
    }

    getWorkflowStatusManagers(status: WorkflowStatus): ManagerCardWorkflowStatus[] {
        return this._workflowStatusManagers.get(status) || [];
    }

    getOverBudgetManagers(budgetCycleId: number): ManagerCardOverBudget[] {
        return this._overBudgetManagers.get(budgetCycleId) || [];
    }

    // For testing
    @action
    setOverBudgetManagers(budgetCycleId: number, managers: ManagerCardOverBudget[]): void {
        this._overBudgetManagers.set(budgetCycleId, managers);
    }

    @action
    setWorkflowStatusManagers(workflowStatus: WorkflowStatus, managers: ManagerCardWorkflowStatus[]): void {
        this._workflowStatusManagers.set(workflowStatus, managers);
    }

    isManagerInWorkflow(id: string, status: WorkflowStatusListType): boolean {
        const converted = maps.workflowStatusFromListTypeMap.get(status) as WorkflowStatus;
        return this.getWorkflowStatusManagers(converted).some(ee =>
            ee.managerSourceId === id
        );
    }

    findManagersWorkflow(id: string): WorkflowStatusListType {
        let foundStatus: WorkflowStatus = WorkflowStatus.Open;
        const found = Array.from(this._workflowStatusManagers.keys()).filter(status =>
            this.isManagerInWorkflow(id, maps.listTypeFromWorkflowStatusMap.get(status) as WorkflowStatusListType ||
             WorkflowStatusListType.Open));
        foundStatus = found[0] || foundStatus;
        return maps.listTypeFromWorkflowStatusMap.get(foundStatus) as WorkflowStatusListType ||
            WorkflowStatusListType.Open;
    }

    @action
    fetchManagerCards(
        sidePanelListType: SidePanelListType,
        listType: string): Promise<void> {
        if (sidePanelListType === SidePanelListType.WorkflowStatus) {
            const workflowStatus = workflowStatusFromListTypeMap.get(listType);
            if (workflowStatus && !this._workflowStatusManagers.get(workflowStatus)) {
                return this.fetchManagerCardWorkflowStatus(workflowStatus);
            }
        } else if (sidePanelListType === SidePanelListType.OverBudget) {
            const budgetCycleId = parseInt(listType, 10);
            if (!isNaN(budgetCycleId) && !this._overBudgetManagers.get(budgetCycleId)) {
                return this.fetchManagerCardOverBudget(budgetCycleId);
            }
        }
        return Promise.resolve();
    }

    @action
    clear(): void {
        this._workflowStatusManagers.clear();
        this._overBudgetManagers.clear();
    }

    get currentCycleId(): number {
        return this._cycleId;
    }

    @action
    private async fetchManagerCardWorkflowStatus(workflowStatus: WorkflowStatus): Promise<void> {
        const workflowStatusManagers = await dataManagerFactory.getManagerDataManager()
            .getWorkflowStatusManagers(
                this._masterAccountId,
                this._cycleId,
                workflowStatus);

        const managerCards = Coalesce.array(workflowStatusManagers || [])
            .map((workflowStatusManager: IWorkflowStatusManager) =>
                new ManagerCardWorkflowStatus(workflowStatusManager));
        this._workflowStatusManagers.set(workflowStatus, managerCards);
    }

    @action
    private async fetchManagerCardOverBudget(budgetCycleId: number): Promise<void> {
        const overBudgetManagers: IManagerCardOverBudget[] = (await dataManagerFactory.getManagerDataManager()
            .getOverBudgetManagers(
                this._masterAccountId,
                this._cycleId,
                budgetCycleId)) || [];

        const managerCards = Coalesce.array(overBudgetManagers)
            .map((overBudgetManager: IManagerCardOverBudget) => new ManagerCardOverBudget(overBudgetManager));
        this._overBudgetManagers.set(budgetCycleId, managerCards);
    }
}